import './ListWrapper.scss'

import { FilterableList } from '@react/FilterableList'
import { ButtonsWrapper } from '@react/FilterableList/Components/ButtonsWrapper'
import { FilterableListContext } from '@react/FilterableList/FilterableListContext'
import { Filterbar } from '@react/Filterbar'
import { ListWrapperProps } from '@react/ListWrapper/ListWrapperTypes'
import LoadingSpinner from '@react/LoadingSpinner'
import { Sortbar } from '@react/Sortbar'
import TeamsNotifications from '@react/TeamsNotifications/TeamsNotifications'
import { cls } from '@react/utils/classname'
import { useEffect, useRef } from 'react'

import { FilterTitle } from './FilterTitle'
import { useListWrapper } from './hooks'

export function ListWrapper(props: ListWrapperProps) {
  const {
    analyticsState,
    filterFieldsState,
    filterTypeState,
    isLoading,
    listStyle,
    notificationsChannel,
    responseData,
    secondaryFilterFieldsState,
    selectedFilters,
    prefix,
    showLoadingSpinnerOnInit,
    totalNumber,
    translationsState,
    urlParams,
    isInitialLoadComplete,
    loadMoreData,
    loadFilterableList,
    onSelectedFiltersChange,
    setAnalyticsState,
    setListStyle,
    setNotificationsChannel,
    setTranslationsState,
    setShowLoadingSpinnerOnInit,
    applyUrlFilters,
  } = useListWrapper(props)

  const { channels = null, title = null, popupTitle = null, hintText = null, hintUrl = null } = notificationsChannel || {}
  const sortable = props?.sortable === 'true'
  const displaySortOrder = props?.displaysortorder === 'true'
  const filterTitle = props.title
  const filterbarVisible = props.filterBarType !== 'none' && filterFieldsState?.length > 0
  const titleVisible = (props.filterBarType !== 'none' && filterTitle) || filterFieldsState?.length > 0
  const componentNameProp = props?.componentName
  const dmProp = props?.dm
  const isLazyLoadEnabled = props?.enableLazyloadList === 'true'
  const showTopRightSeeAll = props.type === 'news' && props.buttonAlignment === 'right' && Boolean(props.seeAllLink) && Boolean(responseData)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasTriggeredLoadRef = useRef(false)
  const filterVisibilityState = (() => {
    try {
      const translationsConfig = JSON.parse(props?.translations || '{}')
      const hasHideFiltersMeta = 'hideFilters' in translationsConfig

      return {
        hideFiltersMeta: hasHideFiltersMeta && translationsConfig.hideFilters === 'true',
        showFiltersMeta: hasHideFiltersMeta && translationsConfig.hideFilters !== 'true',
      }
    } catch (err) {
      // Missing or malformed translations should fall back to the default spacing behavior.
      return {
        hideFiltersMeta: false,
        showFiltersMeta: false,
      }
    }
  })()

  const renderSeeAllButton = () =>
    showTopRightSeeAll ? (
      <FilterableListContext.Provider value={{ isLoading, onLoadMore: loadMoreData, responseData, propsState: props, translationsState }}>
        <ButtonsWrapper url={props.seeAllLink} linkText={translationsState?.filterbarShowAll} />
      </FilterableListContext.Provider>
    ) : null

  useEffect(() => {
    const initializeComponent = async () => {
      try {
        setTranslationsState(JSON.parse(props?.translations))
      } catch (err) {
        setTranslationsState(null)
      }
      try {
        setAnalyticsState(JSON.parse(props?.analytics))
      } catch (err) {
        setAnalyticsState(null)
      }

      await loadFilterableList(false, true) // skipUrlFilters = true

      await applyUrlFilters()
    }

    if (!isLazyLoadEnabled) {
      initializeComponent()
      return
    }

    // Lazy loading: defer API call until component enters the viewport
    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries
        if (entry.isIntersecting && !hasTriggeredLoadRef.current) {
          hasTriggeredLoadRef.current = true
          observer.disconnect()
          setShowLoadingSpinnerOnInit(true)
          initializeComponent()
        }
      },
      { threshold: 0 },
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    try {
      setNotificationsChannel(JSON.parse(props.notificationschannel))
    } catch (err) {
      setNotificationsChannel(null)
    }
  }, [props.notificationschannel])

  useEffect(() => {
    if (isInitialLoadComplete) {
      const parameters = new URLSearchParams(window.location.search)
      if (parameters) {
        const selectedFiltersParams = new URLSearchParams(urlParams)
        selectedFiltersParams.delete(`${prefix}offset`)
        selectedFiltersParams.delete(`${prefix}calculatedOffset`)
        const updatedParams = selectedFiltersParams.size ? `?${selectedFiltersParams.toString()}` : ''
        const relativePath = `${window.location.pathname}${updatedParams || ''}${window.location.hash}`
        history.pushState(null, '', relativePath)
      }
      loadFilterableList()
    }
  }, [urlParams])

  return (
    <>
      <div ref={containerRef} />
      {showLoadingSpinnerOnInit && !responseData?.errorCode && <LoadingSpinner additionalClasses="cmp-filterbar__initial-loading-spinner" />}
      {titleVisible && <FilterTitle title={filterTitle} seoHeadingElement={props.seoHeadingElement} />}
      {filterbarVisible && (
        <div
          data-dm="filterbar.component"
          className={cls({
            'cmp-filterbar__container': true,
            'cmp-filterbar__container--hide-filters-meta': filterVisibilityState.hideFiltersMeta,
            'cmp-filterbar__container--show-filters-meta': filterVisibilityState.showFiltersMeta,
            'cmp-filterbar__quickfilter': filterTypeState === 'quickfilter',
          })}
        >
          <Filterbar
            filterFields={filterFieldsState}
            secondaryFilterFields={secondaryFilterFieldsState}
            selectedFiltersProp={selectedFilters}
            title={filterTitle}
            totalNumber={totalNumber}
            translations={translationsState}
            onSelectedFiltersChange={onSelectedFiltersChange}
            isLoading={isLoading}
            analytics={analyticsState}
            componentName={componentNameProp}
            dataDm={dmProp}
            topActions={renderSeeAllButton()}
          />
          <Sortbar
            sortable={sortable}
            displaySortOrder={displaySortOrder}
            selectedFiltersProp={selectedFilters}
            translations={translationsState}
            onSortSelect={onSelectedFiltersChange}
            listViewTemplates={props.toggleWithList === 'true' ? props.listViewTemplates : undefined}
            onListViewSwitcher={setListStyle}
            disableViewSwitcher={Array.isArray(responseData?.items) && responseData.items.length < 1}
          />
          <div className="cmp-filterbar__separator"></div>
        </div>
      )}
      {showTopRightSeeAll && <div className="list-wrapper__see-all-mobile">{renderSeeAllButton()}</div>}
      {responseData && (
        <FilterableList
          listStyleTemplate={listStyle}
          isLoading={isLoading}
          onLoadMore={loadMoreData}
          responseData={responseData}
          translations={translationsState}
          {...props}
        />
      )}
      {notificationsChannel && (
        <div
          className={cls({
            'list-wrapper__teams-notifications': true,
            'list-wrapper__teams-notifications--has-notifications': true,
          })}
        >
          <TeamsNotifications channels={channels} title={title} popupTitle={popupTitle} hintText={hintText} hintUrl={hintUrl} type="list" />
        </div>
      )}
    </>
  )
}
